/**
 * Dry dock projesinin denetim izi (ADR 0129 Ek Faz 5; Excel Screen Workflow 12, R7 "Auditability",
 * R17). Yeni tablo yoktur: iz var olan kayıtlardan türetilir [NR496 4.6.3]:
 *
 * - **Değişebilir satırlar** (proje, denetim, bulgu, iş): `audit_log`'daki alan değişiklikleri; aynı
 *   kişinin aynı andaki değişiklikleri tek yazma işlemidir (tek yazma yolu bir transaction'ın bütün
 *   satırlarına aynı zamanı yazar) ve tek giriş olur. Ekleme oluşturmadır; durum alanının yeni
 *   değeri iptal, Dock in, Undock, kapanış gibi olayı adlandırır; geri kalanı düzenlemedir
 *   (revizyon: değişen alanlar listelenir).
 * - **Değiştirilemez satırlar** (sonuç ve revizyonu, inceleme, ek, bağ ve iptali, teknik inceleme ve
 *   ofise gönderim, ofis kararı, tersane olayı, doğrulama, bulgu kapanışı ve yeniden açma): satırın
 *   kendisi olaydır; veritabanı katmanı olay olarak verir.
 *
 * Saf fonksiyon: gemi ve ofis aynı satırlardan aynı izi çıkarır. Ofisten inen satırların (karar,
 * doğrulama) gemide `audit_log` kaydı yoktur, ama satır kendisi olay olduğu için izde görünür.
 */

export const DRYDOCK_AUDIT_KINDS = [
  'project_created',
  'project_edited',
  'docked_in',
  'undocked',
  'project_closed',
  'project_cancelled',
  'inspection_opened',
  'inspection_edited',
  'result_recorded',
  'result_corrected',
  'evidence_added',
  'evidence_voided',
  'result_reviewed',
  'inspection_closed',
  'inspection_cancelled',
  'finding_created',
  'finding_edited',
  'finding_reviewed',
  'finding_cancelled',
  'job_created',
  'job_edited',
  'link_added',
  'link_voided',
  'job_submitted',
  'job_approved',
  'job_returned',
  'job_rejected',
  'job_cancelled',
  'job_started',
  'job_progress',
  'job_completed',
  'job_not_done',
  'job_verified',
  'job_verification_returned',
  'job_closed',
  'job_pending',
  'finding_closed',
  'finding_closure_verified',
  'finding_closure_returned',
  'finding_reopened',
] as const;
export type DrydockAuditKind = (typeof DRYDOCK_AUDIT_KINDS)[number];

/** İz süzgeci: proje, denetim, bulgular, işler, tersane ilerlemesi, ofis (karar ve doğrulama). */
export const DRYDOCK_AUDIT_GROUPS = [
  'project',
  'inspection',
  'findings',
  'jobs',
  'yard',
  'office',
] as const;
export type DrydockAuditGroup = (typeof DRYDOCK_AUDIT_GROUPS)[number];

const GROUP: Readonly<Record<DrydockAuditKind, DrydockAuditGroup>> = {
  project_created: 'project',
  project_edited: 'project',
  docked_in: 'project',
  undocked: 'project',
  project_closed: 'project',
  project_cancelled: 'project',
  inspection_opened: 'inspection',
  inspection_edited: 'inspection',
  result_recorded: 'inspection',
  result_corrected: 'inspection',
  evidence_added: 'inspection',
  evidence_voided: 'inspection',
  result_reviewed: 'inspection',
  inspection_closed: 'inspection',
  inspection_cancelled: 'inspection',
  finding_created: 'findings',
  finding_edited: 'findings',
  finding_reviewed: 'findings',
  finding_cancelled: 'findings',
  finding_closed: 'findings',
  finding_reopened: 'findings',
  job_created: 'jobs',
  job_edited: 'jobs',
  link_added: 'jobs',
  link_voided: 'jobs',
  job_submitted: 'jobs',
  job_cancelled: 'jobs',
  job_approved: 'office',
  job_returned: 'office',
  job_rejected: 'office',
  job_verified: 'office',
  job_verification_returned: 'office',
  finding_closure_verified: 'office',
  finding_closure_returned: 'office',
  job_started: 'yard',
  job_progress: 'yard',
  job_completed: 'yard',
  job_not_done: 'yard',
  job_closed: 'yard',
  job_pending: 'yard',
};

export function drydockAuditGroup(kind: DrydockAuditKind): DrydockAuditGroup {
  return GROUP[kind];
}

export type DrydockAuditTable =
  | 'drydock_projects'
  | 'pdi_inspections'
  | 'pdi_findings'
  | 'drydock_jobs';

export type DrydockAuditSubjectType =
  | 'project'
  | 'inspection'
  | 'result'
  | 'attachment'
  | 'finding'
  | 'job'
  | 'link';

export interface DrydockAuditSubject {
  type: DrydockAuditSubjectType;
  id: string;
  /** Okunur kimlik: proje / denetim / bulgu / iş numarası ya da kontrol noktası kodu. */
  label: string | null;
}

/** Değişebilir satırın `audit_log` satırı (alan değişikliği). */
export interface DrydockAuditChange {
  table: DrydockAuditTable;
  rowId: string;
  label: string | null;
  op: string;
  field: string;
  newValue: unknown;
  at: string;
  by: string;
  tzOffsetMin: number | null;
}

/** Değiştirilemez satırdan olay (veritabanı katmanı türünü verir). */
export interface DrydockAuditEvent {
  kind: DrydockAuditKind;
  at: string;
  by: string;
  subject: DrydockAuditSubject;
  /** Gerekçe ya da not. */
  note: string | null;
  /** İlerleme yüzdesi (yalnız `job_progress`). */
  percent: number | null;
  /** Kendi kaydının incelemesi (şirket ayarıyla; sahip kararı 2026-10-26). */
  selfReview: boolean;
  /** Onayda parola yeniden istendi (ADR 0075). */
  reauthenticated: boolean;
  /** Ofis onayı gelmeden başlatılan ek iş (ADR 0129 Ek Faz 4 #1949). */
  provisional: boolean;
  /** Olayı yazan taraf: gemi verisi gemide, karar ve doğrulama ofiste. */
  side: 'ship' | 'office';
  tzOffsetMin: number | null;
}

export interface DrydockAuditEntry extends DrydockAuditEvent {
  /** Düzenlemede değişen alanlar (kolon adları, sıralı). */
  fields: string[];
}

const SUBJECT: Readonly<Record<DrydockAuditTable, DrydockAuditSubjectType>> = {
  drydock_projects: 'project',
  pdi_inspections: 'inspection',
  pdi_findings: 'finding',
  drydock_jobs: 'job',
};

const CREATED: Readonly<Record<DrydockAuditTable, DrydockAuditKind>> = {
  drydock_projects: 'project_created',
  pdi_inspections: 'inspection_opened',
  pdi_findings: 'finding_created',
  drydock_jobs: 'job_created',
};

const EDITED: Readonly<Record<DrydockAuditTable, DrydockAuditKind>> = {
  drydock_projects: 'project_edited',
  pdi_inspections: 'inspection_edited',
  pdi_findings: 'finding_edited',
  drydock_jobs: 'job_edited',
};

const value = (changes: readonly DrydockAuditChange[], field: string) =>
  changes.find((c) => c.field === field)?.newValue;

/** Bir yazma işleminin (aynı satır, kişi ve an) değişikliklerinin türü. */
function kindOf(
  table: DrydockAuditTable,
  changes: readonly DrydockAuditChange[],
): DrydockAuditKind {
  if (changes.some((c) => c.op === 'insert')) return CREATED[table];
  const state = value(changes, 'state');
  if (state === 'cancelled') {
    return table === 'drydock_projects'
      ? 'project_cancelled'
      : table === 'pdi_inspections'
        ? 'inspection_cancelled'
        : table === 'pdi_findings'
          ? 'finding_cancelled'
          : 'job_cancelled';
  }
  if (table === 'pdi_inspections' && state === 'closed') return 'inspection_closed';
  if (table === 'drydock_projects') {
    if (state === 'completed') return 'project_closed';
    if (state === 'in_yard' || (value(changes, 'docked_on') ?? null) !== null) return 'docked_in';
    if ((value(changes, 'undocked_on') ?? null) !== null) return 'undocked';
  }
  return EDITED[table];
}

const text = (v: unknown) => (typeof v === 'string' && v.trim() ? v : null);

const ORDER = new Map<DrydockAuditKind, number>(DRYDOCK_AUDIT_KINDS.map((k, i) => [k, i]));

/** İz: eskiden yeniye; aynı anda türün sırası (oluşturma önce), sonra kayıt etiketi. */
export function drydockAuditTrail(input: {
  changes: readonly DrydockAuditChange[];
  events: readonly DrydockAuditEvent[];
}): DrydockAuditEntry[] {
  const groups = new Map<string, DrydockAuditChange[]>();
  for (const c of input.changes) {
    // Ekleme ayrı işlemdir: aynı milisaniyedeki oluşturma ve düzeltme birleşmez
    const key = `${c.table}\u0000${c.rowId}\u0000${c.at}\u0000${c.by}\u0000${c.op === 'insert'}`;
    const list = groups.get(key);
    if (list) list.push(c);
    else groups.set(key, [c]);
  }
  const entries: DrydockAuditEntry[] = input.events.map((e) => ({ ...e, fields: [] }));
  for (const list of groups.values()) {
    const first = list[0];
    if (!first) continue;
    const kind = kindOf(first.table, list);
    const edited = kind === EDITED[first.table];
    entries.push({
      kind,
      at: first.at,
      by: first.by,
      subject: { type: SUBJECT[first.table], id: first.rowId, label: first.label },
      note:
        text(value(list, 'cancel_reason')) ??
        text(value(list, 'close_note')) ??
        text(value(list, 'close_reason')),
      percent: null,
      selfReview: false,
      reauthenticated: false,
      provisional: false,
      side: 'ship',
      tzOffsetMin: first.tzOffsetMin,
      fields: edited ? [...new Set(list.map((c) => c.field))].sort() : [],
    });
  }
  return entries.sort(
    (a, b) =>
      Date.parse(a.at) - Date.parse(b.at) ||
      (ORDER.get(a.kind) ?? 99) - (ORDER.get(b.kind) ?? 99) ||
      String(a.subject.label ?? '').localeCompare(String(b.subject.label ?? '')) ||
      a.subject.id.localeCompare(b.subject.id),
  );
}
